import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ImageEditModalProps {
  image: {
    id: string;
    prompt: string;
    image_url: string;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditComplete: () => void;
  userId: string;
}

const EDIT_COST = 600;

const EXAMPLE_PROMPTS = [
  "Remove the background",
  "Make it night time",
  "Add snow falling",
  "Change the colors to be more vibrant",
  "Make it look like a painting",
];

export const ImageEditModal = ({
  image,
  open,
  onOpenChange,
  onEditComplete,
  userId,
}: ImageEditModalProps) => {
  const [editPrompt, setEditPrompt] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();

  const handleEdit = async () => {
    if (!image || !editPrompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter an edit instruction",
        variant: "destructive",
      });
      return;
    }

    setIsEditing(true);

    try {
      const { data, error } = await supabase.functions.invoke("edit-image", {
        body: {
          image_url: image.image_url,
          edit_prompt: editPrompt.trim(),
          user_id: userId,
        },
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Edit started",
        description: "Your edited image is being generated...",
      });

      onOpenChange(false);
      setEditPrompt("");
      onEditComplete();
    } catch (error) {
      console.error("Edit error:", error);
      toast({
        title: "Edit failed",
        description: error instanceof Error ? error.message : "Failed to edit image",
        variant: "destructive",
      });
    } finally {
      setIsEditing(false);
    }
  };

  const handleExampleClick = (example: string) => {
    setEditPrompt(example);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Edit Image
          </DialogTitle>
          <DialogDescription>
            Describe what changes you want to make to this image.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Original image preview */}
          {image && (
            <div className="rounded-lg overflow-hidden border bg-muted">
              <img
                src={image.image_url}
                alt="Original"
                className="w-full h-40 object-contain"
              />
            </div>
          )}

          {/* Edit prompt input */}
          <div>
            <Textarea
              placeholder="Describe what to change... e.g., 'Remove the background' or 'Make it sunset'"
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              className="min-h-[80px] resize-none"
              disabled={isEditing}
            />
          </div>

          {/* Example prompts */}
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Try these:</p>
            <div className="flex flex-wrap gap-1.5">
              {EXAMPLE_PROMPTS.map((example) => (
                <button
                  key={example}
                  onClick={() => handleExampleClick(example)}
                  disabled={isEditing}
                  className="text-xs px-2 py-1 rounded-full bg-secondary hover:bg-secondary/80 transition-colors disabled:opacity-50"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          {/* Cost display */}
          <div className="flex items-center justify-between text-sm bg-muted/50 rounded-lg px-3 py-2">
            <span className="text-muted-foreground">Cost</span>
            <span className="font-medium flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              {EDIT_COST} credits
            </span>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isEditing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEdit}
            disabled={isEditing || !editPrompt.trim()}
          >
            {isEditing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Editing...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4 mr-2" />
                Apply Edit
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
